// File: src/lib/graphql/queries/llm.ts
// Purpose: LLM/AI Features GraphQL Queries and Mutations
// VIBE - LLM API

import { gql } from 'graphql-request'
import { executeGraphQL, subscriptionClient } from '../client'

// ============================================
// TYPES
// ============================================

export interface LLMConversation {
  id: string
  title: string
  model: string
  messages: LLMMessage[]
  context?: string
  metadata?: Record<string, unknown>
  isActive: boolean
  createdBy: {
    id: string
    firstName: string
    lastName: string
  }
  createdAt: string
  updatedAt: string
}

export interface LLMMessage {
  id: string
  role: 'user' | 'assistant' | 'system'
  content: string
  timestamp: string
  tokens?: number
  model?: string
  metadata?: Record<string, unknown>
}

export interface LLMApiConfig {
  id: string
  name: string
  provider: 'openai' | 'anthropic' | 'azure' | 'gemini' | 'custom'
  apiKey: string
  baseUrl?: string
  models: string[]
  isDefault: boolean
  maxTokens?: number
  temperature?: number
  topP?: number
  frequencyPenalty?: number
  presencePenalty?: number
  isActive: boolean
  createdAt: string
  updatedAt: string
}

export interface AvailableModel {
  id: string
  name: string
  provider: string
  contextWindow: number
  maxOutputTokens: number
  costPerToken: {
    input: number
    output: number
  }
  capabilities: string[]
  isAvailable: boolean
}

// Helper types for paginated responses
interface Edge<T> {
  node: T
  cursor: string
}

interface PageInfo {
  hasNextPage: boolean
  endCursor?: string
}

interface PaginatedResponse<T> {
  edges: Edge<T>[]
  pageInfo: PageInfo
  totalCount: number
}

interface DeleteResponse {
  success: boolean
  message: string
}

// ============================================
// QUERIES
// ============================================

export const LLM_CONVERSATIONS_QUERY = gql`
  query LLMConversations($first: Int, $after: String, $filter: String) {
    llmConversations(first: $first, after: $after, filter: $filter) {
      edges {
        node {
          id
          title
          model
          messages {
            id
            role
            content
            timestamp
            tokens
          }
          context
          metadata
          isActive
          createdBy {
            id
            firstName
            lastName
          }
          createdAt
          updatedAt
        }
        cursor
      }
      pageInfo {
        hasNextPage
        endCursor
      }
      totalCount
    }
  }
`

export const LLM_CONVERSATION_QUERY = gql`
  query LLMConversation($id: ID!) {
    llmConversation(id: $id) {
      id
      title
      model
      messages {
        id
        role
        content
        timestamp
        tokens
        model
        metadata
      }
      context
      metadata
      isActive
      createdBy {
        id
        firstName
        lastName
        email
      }
      createdAt
      updatedAt
    }
  }
`

export const LLM_API_CONFIGS_QUERY = gql`
  query LLMApiConfigs {
    llmApiConfigs {
      id
      name
      provider
      baseUrl
      models
      isDefault
      maxTokens
      temperature
      topP
      frequencyPenalty
      presencePenalty
      isActive
      createdAt
      updatedAt
    }
  }
`

export const AVAILABLE_MODELS_QUERY = gql`
  query AvailableModels {
    availableModels {
      id
      name
      provider
      contextWindow
      maxOutputTokens
      costPerToken {
        input
        output
      }
      capabilities
      isAvailable
    }
  }
`

// ============================================
// MUTATIONS
// ============================================

export const CREATE_LLM_CONVERSATION_MUTATION = gql`
  mutation CreateLLMConversation($input: CreateLLMConversationInput!) {
    createLLMConversation(input: $input) {
      id
      title
      model
      messages {
        id
        role
        content
        timestamp
      }
      context
      isActive
      createdAt
    }
  }
`

export const SEND_MESSAGE_MUTATION = gql`
  mutation SendMessage($conversationId: ID!, $message: String!, $context: String) {
    sendMessage(conversationId: $conversationId, message: $message, context: $context) {
      id
      messages {
        id
        role
        content
        timestamp
        tokens
      }
      updatedAt
    }
  }
`

export const UPDATE_CONVERSATION_TITLE_MUTATION = gql`
  mutation UpdateConversationTitle($id: ID!, $title: String!) {
    updateConversationTitle(id: $id, title: $title) {
      id
      title
      updatedAt
    }
  }
`

export const DELETE_LLM_CONVERSATION_MUTATION = gql`
  mutation DeleteLLMConversation($id: ID!) {
    deleteLLMConversation(id: $id) {
      success
      message
    }
  }
`

export const CREATE_LLM_API_CONFIG_MUTATION = gql`
  mutation CreateLLMApiConfig($input: CreateLLMApiConfigInput!) {
    createLLMApiConfig(input: $input) {
      id
      name
      provider
      models
      isDefault
      isActive
      createdAt
    }
  }
`

export const UPDATE_LLM_API_CONFIG_MUTATION = gql`
  mutation UpdateLLMApiConfig($id: ID!, $input: UpdateLLMApiConfigInput!) {
    updateLLMApiConfig(id: $id, input: $input) {
      id
      name
      provider
      models
      maxTokens
      temperature
      isDefault
      isActive
      updatedAt
    }
  }
`

export const DELETE_LLM_API_CONFIG_MUTATION = gql`
  mutation DeleteLLMApiConfig($id: ID!) {
    deleteLLMApiConfig(id: $id) {
      success
      message
    }
  }
`

export const SET_DEFAULT_LLM_API_CONFIG_MUTATION = gql`
  mutation SetDefaultLLMApiConfig($id: ID!) {
    setDefaultLLMApiConfig(id: $id) {
      id
      isDefault
    }
  }
`

// ============================================
// SUBSCRIPTIONS
// ============================================

export const MESSAGE_STREAM_SUBSCRIPTION = gql`
  subscription MessageStream($conversationId: ID!) {
    messageStream(conversationId: $conversationId) {
      id
      role
      content
      timestamp
      tokens
    }
  }
`

// ============================================
// API SERVICE CLASS
// ============================================

export class LLMAPI {
  // Conversation Queries
  static async getConversations(
    first: number = 10,
    after?: string,
    filter?: string
  ): Promise<PaginatedResponse<LLMConversation>> {
    const { llmConversations } = await executeGraphQL<{ llmConversations: PaginatedResponse<LLMConversation> }>(
      LLM_CONVERSATIONS_QUERY,
      { first, after, filter }
    )
    return llmConversations
  }

  static async getConversation(id: string): Promise<LLMConversation> {
    const { llmConversation } = await executeGraphQL<{ llmConversation: LLMConversation }>(
      LLM_CONVERSATION_QUERY,
      { id }
    )
    return llmConversation
  }

  static async getApiConfigs(): Promise<LLMApiConfig[]> {
    const { llmApiConfigs } = await executeGraphQL<{ llmApiConfigs: LLMApiConfig[] }>(
      LLM_API_CONFIGS_QUERY
    )
    return llmApiConfigs
  }

  static async getAvailableModels(): Promise<AvailableModel[]> {
    const { availableModels } = await executeGraphQL<{ availableModels: AvailableModel[] }>(
      AVAILABLE_MODELS_QUERY
    )
    return availableModels
  }

  // Conversation Mutations
  static async createConversation(input: Partial<LLMConversation>): Promise<LLMConversation> {
    const { createLLMConversation } = await executeGraphQL<{ createLLMConversation: LLMConversation }>(
      CREATE_LLM_CONVERSATION_MUTATION,
      { input }
    )
    return createLLMConversation
  }

  static async sendMessage(
    conversationId: string,
    message: string,
    context?: string
  ): Promise<LLMConversation> {
    const { sendMessage } = await executeGraphQL<{ sendMessage: LLMConversation }>(
      SEND_MESSAGE_MUTATION,
      { conversationId, message, context }
    )
    return sendMessage
  }

  static async updateConversationTitle(id: string, title: string): Promise<LLMConversation> {
    const { updateConversationTitle } = await executeGraphQL<{ updateConversationTitle: LLMConversation }>(
      UPDATE_CONVERSATION_TITLE_MUTATION,
      { id, title }
    )
    return updateConversationTitle
  }

  static async deleteConversation(id: string): Promise<DeleteResponse> {
    const { deleteLLMConversation } = await executeGraphQL<{ deleteLLMConversation: DeleteResponse }>(
      DELETE_LLM_CONVERSATION_MUTATION,
      { id }
    )
    return deleteLLMConversation
  }

  // API Config Mutations
  static async createApiConfig(input: Partial<LLMApiConfig>): Promise<LLMApiConfig> {
    const { createLLMApiConfig } = await executeGraphQL<{ createLLMApiConfig: LLMApiConfig }>(
      CREATE_LLM_API_CONFIG_MUTATION,
      { input }
    )
    return createLLMApiConfig
  }

  static async updateApiConfig(id: string, input: Partial<LLMApiConfig>): Promise<LLMApiConfig> {
    const { updateLLMApiConfig } = await executeGraphQL<{ updateLLMApiConfig: LLMApiConfig }>(
      UPDATE_LLM_API_CONFIG_MUTATION,
      { id, input }
    )
    return updateLLMApiConfig
  }

  static async deleteApiConfig(id: string): Promise<DeleteResponse> {
    const { deleteLLMApiConfig } = await executeGraphQL<{ deleteLLMApiConfig: DeleteResponse }>(
      DELETE_LLM_API_CONFIG_MUTATION,
      { id }
    )
    return deleteLLMApiConfig
  }

  static async setDefaultApiConfig(id: string): Promise<LLMApiConfig> {
    const { setDefaultLLMApiConfig } = await executeGraphQL<{ setDefaultLLMApiConfig: LLMApiConfig }>(
      SET_DEFAULT_LLM_API_CONFIG_MUTATION,
      { id }
    )
    return setDefaultLLMApiConfig
  }

  // Subscription Methods
  static subscribeToMessageStream(
    conversationId: string,
    handler: (message: LLMMessage) => void
  ): () => void {
    const subscriptionId = `message-stream-${conversationId}`

    subscriptionClient.subscribe(
      subscriptionId,
      MESSAGE_STREAM_SUBSCRIPTION.replace('$conversationId', conversationId),
      (data: unknown) => {
        const payload = data as { messageStream?: LLMMessage }
        if (payload && payload.messageStream) {
          handler(payload.messageStream)
        }
      }
    )

    // Return unsubscribe function
    return () => {
      subscriptionClient.unsubscribe(subscriptionId)
    }
  }
}